import { useCallback, useMemo } from 'react';

/**
 * useNestedSprints Hook
 * 
 * Manages nested sprint navigation, creation, and breadcrumb path.
 */
export const useNestedSprints = ({
  localSprint,
  setLocalSprint,
  allSprints,
  onSelectSprint,
  onAddSprint,
  onUpdateSprint,
  onCreateNestedSprint,
}) => {
  // Nested campaigns that belong to this campaign. Once missions are loaded,
  // only keep rows that a mission actually contains — parentSprintId ghosts
  // must not appear as flow nodes or sidebar children.
  const nestedSprints = useMemo(() => {
    const children = allSprints.filter((s) => s.parentSprintId === localSprint?.id && !s.deletedAt);
    if (!Array.isArray(localSprint?.tasks)) return children;
    const linkedIds = new Set(
      localSprint.tasks.filter((t) => t?.nestedSprintId).map((t) => t.nestedSprintId)
    );
    return children.filter((s) => linkedIds.has(s.id));
  }, [allSprints, localSprint?.id, localSprint?.tasks]);

  // Get breadcrumb path (for nested sprint navigation)
  const breadcrumbPath = useMemo(() => {
    if (!localSprint?.parentSprintId) return [];
    
    const path = [];
    let currentId = localSprint.parentSprintId;
    
    while (currentId) {
      const parent = allSprints.find(s => s.id === currentId);
      if (parent) {
        path.unshift(parent);
        currentId = parent.parentSprintId;
      } else {
        break;
      }
    }
    
    return path;
  }, [localSprint?.parentSprintId, allSprints]);

  // Handle opening a nested sprint
  const handleOpenNestedSprint = useCallback((nestedSprint) => {
    if (onSelectSprint) {
      onSelectSprint(nestedSprint);
    } else {
      console.warn('onSelectSprint is not defined - cannot navigate to nested sprint');
    }
  }, [onSelectSprint]);

  // Handle creating a nested sprint for a task
  // selectSprintOptions — forwarded to onSelectSprint (e.g. { view: 'table' } to stay in table view)
  const handleCreateNestedSprintForTask = useCallback((taskId, selectSprintOptions) => {
    // onCreateNestedSprint now handles orphaned nestedSprintId cleanup internally
    const newSprint = onCreateNestedSprint(taskId);
    
    if (newSprint && typeof newSprint === 'object' && onAddSprint) {
      // Flush the parent mission link to global state before POSTing the
      // nested campaign, so it can never exist without a container mission.
      if (localSprint && onUpdateSprint) {
        onUpdateSprint({
          ...localSprint,
          tasks: (localSprint.tasks || []).map((t) =>
            t.id === taskId ? { ...t, nestedSprintId: newSprint.id, icon: null } : t
          ),
        });
      }
      onAddSprint(newSprint);

      setTimeout(() => {
        onSelectSprint?.(newSprint, selectSprintOptions);
      }, 100);
    } else if (typeof newSprint === 'string') {
      // It returned an existing sprint ID - this shouldn't happen anymore
      // but handle gracefully by opening the existing sprint
      const existingSprint = allSprints.find(s => s.id === newSprint);
      if (existingSprint) {
        onSelectSprint?.(existingSprint, selectSprintOptions);
      }
    }
  }, [onCreateNestedSprint, onAddSprint, onSelectSprint, onUpdateSprint, localSprint, allSprints]);

  // Handle going back to parent sprint
  const handleGoToParent = useCallback(() => {
    if (localSprint?.parentSprintId && onSelectSprint) {
      const parent = allSprints.find(s => s.id === localSprint.parentSprintId);
      if (parent) {
        onSelectSprint(parent);
      }
    }
  }, [localSprint?.parentSprintId, allSprints, onSelectSprint]);

  return {
    nestedSprints,
    breadcrumbPath,
    handleOpenNestedSprint,
    handleCreateNestedSprint: handleCreateNestedSprintForTask,
    handleGoToParent,
  };
};

export default useNestedSprints;

